import React, { FormEvent, useState, useContext } from "react";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useRouter } from "next/router";
import { toSafeInteger, isEmpty } from "lodash";
import Dots from "components/componentLibrary/LoadingDots";
import SignInModal from "components/componentLibrary/Modals/SignInModal";
import { constantCase } from "util/strings";
import { areaQueryWithSeoString_area } from "util/seo/types/areaQueryWithSeoString";
import useIsCityInDb from "hooks/useIsCityInDb";
import { EMAIL_REGEX, LEASE_TYPE } from "util/constants";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import {
  CreateRenterSearchActiveRequirementsMutation,
  determineLeaseTerm,
} from "components/ClientSearch/Filters/SaveActiveRequirementsButton";
import { useCurrentUser } from "hooks";
import { trackChatStart } from "util/ga4";

import { BuildingPageQuery_buildingOrRedirect_building_neighborhood } from "types/pages/building/BuildingPageQuery";
import styles from "./styles.module.scss";

export const RENTER_ONBOARDING_MUTATION = gql`
  mutation RenterOnboardingChatCTAMutation(
    $email: String!
    $phone: String
    $cityOfInterest: CityName
    $marketOfInterest: ID
    $numberOfEmployees: Int
    $squareFeet: Int
    $neighborhoodIds: [ID]
    $neighborhoodNames: [String]
    $signUpType: String
    $areaExternalIds: [ID]
    $source: String
  ) {
    renterOnboarding(
      input: {
        renter: { phone: $phone, email: $email, signUpType: $signUpType }
        cityOfInterest: $cityOfInterest
        marketOfInterest: $marketOfInterest
        areaExternalIds: $areaExternalIds
        onboardingInfo: {
          neighborhoodIds: $neighborhoodIds
          numberOfEmployees: $numberOfEmployees
          squareFeet: $squareFeet
          neighborhoodNames: $neighborhoodNames
          source: $source
        }
      }
    ) {
      onboardingId
      created
      neighborhoodNames
      currentUser {
        id
        salesforceId
        email
        uuid
        firstName
        lastName
        phone
        e164Phone
        companyName
        displayName
        isRenter
        isBroker
        isVerified
        isAdmin
        hasPassword
        featureFlags
        profilePicURL
        brokerMarket {
          id
          name
          state
        }
        completedUserTasks {
          id
          task
        }
        confirmed
        unconfirmedEmail
        cityOfInterest
        marketOfInterest {
          id
          name
          position {
            lat
            lng
          }
        }
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          surveys {
            nodes {
              id
              externalId
            }
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              position {
                lat
                lng
              }
            }
          }
        }
      }
      errors {
        auth
      }
    }
  }
`;

interface FormElements extends HTMLFormControlsCollection {
  chatCtaEmail: HTMLInputElement;
}

interface ChatCtaFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

interface IProps {
  areas: (
    | areaQueryWithSeoString_area
    | BuildingPageQuery_buildingOrRedirect_building_neighborhood
  )[];
  market: string;
  city?: string;
  signUpType: string;
  address: string;
  buildingSlug?: string;
  listingId?: string;
  isLoggedIn: boolean;
}

const listingLinkPrefix = "https://www.squarefoot.com/tourbook/listing/";
const buildingingLinkPrefix = "https://www.squarefoot.com/building";

const ChatCTA = ({
  areas,
  market,
  city,
  signUpType,
  address,
  buildingSlug,
  listingId,
  isLoggedIn = false,
}: IProps) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [onboardRenter, { loading }] = useMutation(RENTER_ONBOARDING_MUTATION);
  const [createRenterSearchActiveRequirements] = useMutation(
    CreateRenterSearchActiveRequirementsMutation
  );
  const router = useRouter();
  const isCityInDB = useIsCityInDb(city);
  const { values } = useContext(QueryManagerContext);
  const { login } = useCurrentUser();

  const getRedirectLink = () => {
    const link = listingId
      ? listingLinkPrefix + listingId
      : buildingingLinkPrefix + buildingSlug;
    const messageSuffix =
      listingId || buildingSlug ? ` (${address}, ${link})` : "";
    return `/tourbook/messages?message=${message}${messageSuffix}`;
  };

  const onSubmit = async (evt: FormEvent<ChatCtaFormElement>) => {
    evt.preventDefault();

    trackChatStart({
      listingId: listingId || "",
      pageUrl: router.asPath,
      buildingName: address,
      city: city || "",
    });

    if (isLoggedIn) {
      router.push(getRedirectLink());
      return;
    }

    const variables = {
      email,
      marketOfInterest: market,
      cityOfInterest: city && !market && isCityInDB ? constantCase(city) : null,
      areaExternalIds: areas.map(({ externalId }) => externalId),
      signUpType,
      source: "Chat",
    };

    const { data } = await onboardRenter({ variables });
    if (
      !data?.renterOnboarding?.created &&
      data?.renterOnboarding?.errors?.auth?.includes(
        "Login and password do not match."
      )
    ) {
      setShowLoginModal(true);
    } else if (data?.renterOnboarding?.currentUser) {
      if (!isEmpty(values) && router.asPath.includes("office-space/m/"))
        createRenterSearchActiveRequirements({
          variables: {
            marketExternalId: values.market.externalId,
            leaseTerm: determineLeaseTerm({
              leaseTermMin: values.leaseTermMin,
              leaseTermMax: values.leaseTermMax,
            }),
            statuses: [
              "available",
              "offers_in",
              "squarefoot_offers_in",
              "lease_out",
              "squarefoot_lease_out",
            ],
            leaseTypes: [...Object.keys(LEASE_TYPE), "UNKNOWN"],
            campaignId: data?.renterOnboarding?.currentUser.latestCampaign.id,
            minPricePerSquareFoot: values.minPricePerSquareFoot,
            maxPricePerSquareFoot: values.maxPricePerSquareFoot,
            minSquareFeet: values.minSqft,
            maxSquareFeet: values.maxSqft,
            minMonthlyBudget: values.minMonthlyPrice,
            maxMonthlyBudget: values.maxMonthlyPrice,
            numberOfEmployees: toSafeInteger(values.numEmployees),
            areaExternalIds: [
              ...values.submarkets.map(
                ({ externalId }: areaQueryWithSeoString_area) => externalId
              ),
              ...values.neighborhoods.map(
                ({ externalId }: areaQueryWithSeoString_area) => externalId
              ),
            ],
          },
        });
      await login();
      router.push(getRedirectLink());
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} className={styles.form} data-qa="chatCTAForm">
        <div className={styles.inputContainer}>
          {!isLoggedIn && (
            <input
              type="email"
              name="chatCtaEmail"
              id="chatCtaEmail"
              data-qa="chatCTAEmail"
              pattern={EMAIL_REGEX}
              title={`${email} is not a valid email`}
              className={styles.email}
              placeholder="Your email"
              required
              value={email}
              onChange={(evt) => setEmail(evt.currentTarget.value)}
            />
          )}
          <textarea
            name="chatCta"
            id="chatCta"
            data-qa="chatCTAMessage"
            className={styles.chatInput}
            placeholder="Have questions or looking to tour this space? Enter your message."
            value={message}
            onChange={(evt) => setMessage(evt.currentTarget.value)}
          />
          <button
            type="submit"
            data-qa="chatCTASubmit"
            disabled={loading}
            className={`btn-primary ${styles.btn}`}
          >
            {loading ? (
              <>
                Starting Chat <Dots />
              </>
            ) : (
              <>Start Chat</>
            )}
          </button>
        </div>
      </form>

      {showLoginModal && (
        <SignInModal
          existingEmail={email}
          onSuccess={() => {
            setShowLoginModal(false);
            router.push(getRedirectLink());
          }}
          onClose={() => {
            setShowLoginModal(false);
          }}
        />
      )}
    </>
  );
};

export default ChatCTA;
